import { useThemeStore } from "@stores/theme.store";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  LuAlertCircle,
  LuBanknote,
  LuCheckCircle2,
  LuChevronDown,
  LuChevronUp,
  LuCopy,
  LuReceipt,
  LuXCircle,
} from "react-icons/lu";
import copy from "copy-to-clipboard";
import moment from "moment";
import { Button, message } from "antd";
import { toRp } from "@utils/helper";
import { PopupConfirm } from "@components/general/popup";
import { NoticeType } from "antd/es/message/interface";
import { useMutation } from "@tanstack/react-query";
import { FinanceService } from "@services/finance.service";
import { DetailRequestTagihanVA } from "@services/dtos/finance.dto";

const statusConfig = {
  PENDING: {
    icon: <LuAlertCircle className="tw-text-2xl tw-text-[#f59e0b]" />,
    label: "Menunggu Pembayaran",
    bg: "#fffbeb",
    text: "#f59e0b",
    ring: "#fde68a",
  },
  EXPIRED: {
    icon: <LuXCircle className="tw-text-2xl tw-text-[#ef4444]" />,
    label: "Kadaluarsa",
    bg: "#fef2f2",
    text: "#ef4444",
    ring: "#fecaca",
  },
  COMPLETED: {
    icon: <LuCheckCircle2 className="tw-text-2xl tw-text-[#22c55e]" />,
    label: "Pembayaran Berhasil",
    bg: "#f0fdf4",
    text: "#22c55e",
    ring: "#bbf7d0",
  },
};

type InfoRowProps = {
  label: string;
  value: React.ReactNode;
};

const InfoRow = ({ label, value }: InfoRowProps) => (
  <div className="tw-flex tw-justify-between tw-items-center tw-py-2.5">
    <span className="tw-text-xs tw-text-text-secondary tw-w-2/5">{label}</span>
    <span className="tw-text-sm tw-font-semibold tw-text-text-primary tw-text-right tw-w-3/5">
      {value}
    </span>
  </div>
);

const Divider = () => (
  <div className="tw-h-px" style={{ background: "#f1f1f1" }} />
);

export const VaDetailPage = () => {
  const { state } = useLocation();
  const { setHeader, setAppBar, setBottomNavigation } = useThemeStore(
    (s) => s,
  );
  const [messageApi, contextHolder] = message.useMessage();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [showMessageText, setShowMessageText] = useState<string>("");
  const [messageType, setMessageType] = useState<string>("");
  const [trxID, setTrxID] = useState<string>("");
  const [popupTitle, setPopupTitle] = useState<string>("");
  const [popupMessage, setPopupMessage] = useState<string>("");
  const [popupTextConfirm, setPopupTextConfirm] = useState<string>("");
  const [popupCancel, setPopupCancel] = useState<boolean>(false);
  const [status, setStatus] = useState<string>(state?.vaStatus);
  const [detailOpen, setDetailOpen] = useState<boolean>(false);

  const cfg = statusConfig[status as keyof typeof statusConfig] ?? statusConfig.EXPIRED;

  const totalAmount =
    state?.vaTrxAmount === 0
      ? state?.vaBankFee ?? 0
      : state.vaBankName?.toLowerCase() === "bni"
      ? (state?.vaTrxAmount ?? 0) + (state?.vaBankFee ?? 0)
      : state?.vaTrxAmount ?? 0;

  const nominalAmount =
    state.vaBankName?.toLowerCase() === "bni"
      ? state?.vaTrxAmount
      : state?.vaTrxAmount - state?.vaBankFee;

  const detailCount =
    (state.detail?.tagihan?.length ?? 0) +
    (state.detail?.uangsaku != "0" ? 1 : 0);

  const { mutate, isPending } = useMutation({
    mutationFn: FinanceService.virtualAccountCancel,
    onSuccess: (response) => {
      if (response.success) {
        setStatus("EXPIRED");
        setTrxID("");
        setShowMessage(true);
        setShowMessageText("Virtual account berhasil dibatalkan!");
        setMessageType("success");
      } else {
        setShowMessage(true);
        setShowMessageText(response.message);
        setMessageType("error");
      }
    },
    onError: (error: any) => {
      setShowMessage(true);
      setShowMessageText(error.message ?? "Internal Error");
      setMessageType("error");
    },
  });

  const handleConfirm = () => {
    setPopupCancel(false);
    mutate(trxID);
  };

  const onCopy = (text: string | number, label: string) => {
    copy(String(text));
    setShowMessage(true);
    setShowMessageText(`${label} berhasil disalin!`);
    setMessageType("success");
  };

  const onOpenPopupCancel = (id: string) => {
    setTrxID(id);
    setPopupTitle("Batalkan Pembayaran");
    setPopupMessage(
      "Jika dibatalkan maka status transaksi menjadi EXPIRED dan TIDAK BISA DIGUNAKAN KEMBALI",
    );
    setPopupTextConfirm("Ya, Batalkan");
    setPopupCancel(true);
  };

  useEffect(() => {
    setAppBar({ title: "Detail VA", show: true, leading: "back" });
    setHeader({ buttonBack: true, filter: false, title: "Detail VA", image: false });
    setBottomNavigation("hidden");
  }, [setHeader, setAppBar, setBottomNavigation]);

  useEffect(() => {
    if (showMessage) {
      messageApi.open({ type: messageType as NoticeType, content: <>{showMessageText}</> });
    }
    setShowMessage(false);
  }, [showMessage, showMessageText, messageType, messageApi]);

  return (
    <>
      {contextHolder}
      <div className="tw-px-4 tw-pt-2 tw-pb-20 tw-space-y-3">

        {/* Status + Total hero card */}
        <div className="card-body tw-text-center tw-py-6">
          <div
            className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-rounded-full tw-mb-4"
            style={{ background: cfg.bg, border: `1px solid ${cfg.ring}` }}
          >
            {cfg.icon}
            <span className="tw-text-xs tw-font-semibold" style={{ color: cfg.text }}>
              {cfg.label}
            </span>
          </div>

          <div className="tw-text-3xl tw-font-bold tw-text-text-primary tw-mb-1">
            {toRp(totalAmount)}
          </div>
          <div className="tw-text-xs tw-text-text-secondary">
            {status === "COMPLETED"
              ? moment(state.vaDatePayment ?? Date.now()).format("dddd, DD MMM YYYY · HH:mm")
              : moment(state.vaDateExpired ?? Date.now()).format("dddd, DD MMM YYYY · HH:mm")}
          </div>
        </div>

        {/* VA Number card */}
        <div className="card-body">
          <div className="tw-text-xs tw-font-bold tw-text-text-secondary tw-uppercase tw-tracking-widest tw-mb-3">
            Nomor Virtual Account
          </div>
          <div
            className="tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-px-4 tw-py-3 tw-cursor-pointer"
            style={{ background: "#f0fdf4", border: "1.5px solid #bbf7d0" }}
            onClick={() => onCopy(state?.vaNumber, "Nomor VA")}
          >
            <span className="tw-text-base tw-font-bold tw-text-[#15803d] tw-tracking-wider">
              {state?.vaNumber ?? "-"}
            </span>
            <LuCopy className="tw-text-lg tw-text-[#22c55e] tw-shrink-0" />
          </div>
          <div className="tw-mt-2 tw-text-xs tw-text-text-secondary tw-text-center">
            {state.vaBankName} · {state.vaName}
          </div>
        </div>

        {/* Transaction detail card */}
        <div className="card-body">
          <div className="tw-text-xs tw-font-bold tw-text-text-secondary tw-uppercase tw-tracking-widest tw-mb-1">
            Rincian Transaksi
          </div>

          <InfoRow label="ID Transaksi" value={
            <span className="tw-flex tw-items-center tw-justify-end tw-gap-1">
              <LuReceipt className="tw-text-text-secondary tw-shrink-0" />
              {state.vaTrxId ?? "-"}
            </span>
          } />
          <Divider />
          <InfoRow label="Nominal" value={toRp(nominalAmount)} />
          <Divider />
          <InfoRow label="Biaya Admin" value={toRp(state?.vaBankFee ?? 0)} />
          <Divider />
          <div className="tw-flex tw-justify-between tw-items-center tw-pt-3 tw-pb-1">
            <span className="tw-text-sm tw-font-bold tw-text-text-primary">Total</span>
            <div
              className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer"
              onClick={() => onCopy(totalAmount, "Nominal Pembayaran")}
            >
              <span className="tw-text-base tw-font-bold tw-text-primary">
                {toRp(totalAmount)}
              </span>
              <LuCopy className="tw-text-base tw-text-primary" />
            </div>
          </div>
          <div className="tw-text-[10px] tw-text-text-secondary tw-text-right">
            ketuk nominal untuk menyalin
          </div>
        </div>

        {/* Detail accordion card */}
        {detailCount > 0 && (
          <div className="card-body">
            <div
              className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer"
              onClick={() => setDetailOpen((p) => !p)}
            >
              <div className="tw-flex tw-items-center tw-gap-2">
                <LuBanknote className="tw-text-primary" />
                <span className="tw-text-sm tw-font-semibold tw-text-text-primary">
                  Detail Tagihan
                </span>
                <span className="tw-text-xs tw-px-1.5 tw-py-0.5 tw-rounded-full tw-bg-primary tw-text-white tw-font-bold">
                  {detailCount}
                </span>
              </div>
              {detailOpen
                ? <LuChevronUp className="tw-text-text-secondary" />
                : <LuChevronDown className="tw-text-text-secondary" />}
            </div>

            {detailOpen && (
              <div className="tw-mt-3 tw-space-y-0">
                {state.detail?.uangsaku != "0" && (
                  <>
                    <Divider />
                    <InfoRow label="Uang Saku" value={toRp(
                      parseFloat((state.detail.uangsaku ?? "0").replace(",", ""))
                    )} />
                  </>
                )}
                {state.detail?.tagihan?.map(
                  (item: DetailRequestTagihanVA, idx: number) => (
                    <div key={idx}>
                      <Divider />
                      <InfoRow
                        label={item.tagihan}
                        value={toRp(parseFloat((item.dibayar ?? "0").replace(",", "")))}
                      />
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}

        {/* Cancel button */}
        {status === "PENDING" && (
          <Button
            loading={isPending}
            className="tw-h-11 tw-w-full tw-text-sm tw-font-semibold tw-rounded-xl tw-border-red-500 tw-text-red-500 focus:tw-shadow-none hover:tw-bg-red-50"
            onClick={() => onOpenPopupCancel(state?.vaTrxId ?? "")}
          >
            Batalkan Pembayaran
          </Button>
        )}
      </div>

      <PopupConfirm
        open={popupCancel}
        title={popupTitle}
        message={popupMessage}
        textConfirm={popupTextConfirm}
        textCancel="Kembali"
        onConfirm={() => handleConfirm()}
        onCancel={() => setPopupCancel(false)}
      />
    </>
  );
};
